import { selectAccountId } from '../../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../../store/hooks'
import { useCompleteIntegrationWorkflowStepMutation, useStartIntegrationWorkflowMutation } from '../../../../../../store/integrations/integrations'
import { TrackEvent, useTracking } from '../../../common/tracking'
import {
  type CompleteIntegrationWorkflowStepInput,
  type IntegrationWorkflowTarget,
  type StartIntegrationWorkflowInput,
  workflowStepEntityId,
} from './integrationWorkflow'
import { getIntegrationWorkflowAdapter } from './integrationWorkflowAdapters'

export const useStartIntegrationWorkflow = ({ serviceId, externalId, contactId }: IntegrationWorkflowTarget) => {
  const accountId = useAppSelector(selectAccountId)
  const { tracked } = useTracking()
  const [startWorkflow, state] = useStartIntegrationWorkflowMutation()

  const start: (input: StartIntegrationWorkflowInput) => ReturnType<typeof startWorkflow> = tracked(
    ({ payload }: StartIntegrationWorkflowInput) => startWorkflow({ accountId, connector: serviceId, externalId, payload }),
    { eventName: TrackEvent.START_NEW_WORKFLOW_IN_CRM },
    { eventDetailsExtractor: ({ templateId }: StartIntegrationWorkflowInput) => ({ connector: serviceId, contactId, templateId }) },
  )

  return [start, state] as const
}

export const useCompleteIntegrationWorkflowStep = ({ serviceId, externalId, contactId }: IntegrationWorkflowTarget) => {
  const accountId = useAppSelector(selectAccountId)
  const { tracked } = useTracking()
  const [completeWorkflowStep, state] = useCompleteIntegrationWorkflowStepMutation()

  const complete: (workflowId: number, stepId: number, input: CompleteIntegrationWorkflowStepInput) => ReturnType<typeof completeWorkflowStep> = tracked(
    (workflowId: number, stepId: number, input: CompleteIntegrationWorkflowStepInput) =>
      completeWorkflowStep({
        accountId,
        connector: serviceId,
        externalId,
        entityId: workflowStepEntityId(workflowId, stepId),
        payload: getIntegrationWorkflowAdapter(serviceId).buildCompleteStepPayload(input),
      }),
    { eventName: TrackEvent.UPDATE_WORKFLOW_STEP_IN_CRM },
    {
      eventDetailsExtractor: (_workflowId: number, _stepId: number, input: CompleteIntegrationWorkflowStepInput) => ({
        connector: serviceId,
        contactId,
        workflowOutcomeId: input.outcomeId,
      }),
    },
  )

  return [complete, state] as const
}
